
import React from 'react';
import { useTheme } from '@mui/material/styles';
import {
  PageContainer,
  PageTitle,
  NotificationCard,
  CardHeader,
  NotificationTitle,
  NotificationTime,
  NotificationBody,
  NotificationFooter
} from './styles';
import { useDispatch, useSelector } from 'react-redux';
import { selectNotifications, selectNotificationNext, selectNotificationLoading } from '../../modules/notifications/selectors';
import { fetchNotifications, markNotificationAsRead } from '../../modules/notifications/actions';
import { INotification as ReduxNotification } from '../../modules/notifications/types';
import { AppDispatch } from '../../store';
import { useEffect } from 'react';



const FacilityNotification: React.FC = () => {
  const theme = useTheme();
  const dispatch = useDispatch<AppDispatch>();
  const notifications = useSelector(selectNotifications);
  const next = useSelector(selectNotificationNext);
  const loading = useSelector(selectNotificationLoading);
  const [page, setPage] = React.useState(1);
  const observerTarget = React.useRef(null);

  useEffect(() => {
    // Initial fetch
    if (page === 1) {
      dispatch(fetchNotifications({ page: 1, page_size: 10 }));
    }
  }, [dispatch, page]);

  useEffect(() => {
    const currentTarget = observerTarget.current;

    const observer = new IntersectionObserver(
      entries => {
        if (entries[0].isIntersecting && next && !loading) {
          const nextPage = page + 1;
          setPage(nextPage);
          dispatch(fetchNotifications({ page: nextPage, page_size: 10 }));
        }
      },
      { threshold: 1.0 }
    );

    if (currentTarget) {
      observer.observe(currentTarget);
    }

    return () => {
      if (currentTarget) {
        observer.unobserve(currentTarget);
      }
    };
  }, [next, loading, page, dispatch]);

  const getStatusColor = (type: string) => {
    switch (type) {
      case 'shift_assignment':
      case 'shift_accepted':
      case 'success':
        return theme.palette.primary.main; // Green
      case 'worker_no_show':
      case 'warning':
        return theme.palette.warning.main; // Yellow/Orange
      case 'facility_onboarding':
      case 'info':
        return '#2196F3'; // Blue
      case 'shift_cancellation':
      case 'error':
        return theme.palette.error.main; // Red
      default:
        return theme.palette.grey[500];
    }
  };

  return (
    <PageContainer>
      <PageTitle variant="h4">Notifications</PageTitle>
      {notifications.map((notification: ReduxNotification) => (
        <NotificationCard
          key={notification.notification_id}
          statusColor={getStatusColor(notification.notification_type)}
          isRead={notification.is_read}
          onClick={() => {
            if (!notification.is_read) {
              dispatch(markNotificationAsRead(notification.notification_id))
                .unwrap()
                .then(() => {
                  dispatch(fetchNotifications({ page: page, page_size: 10 }));
                });
            }
          }}
          style={{ cursor: 'pointer' }}
        >
          <CardHeader>
            <NotificationTitle>{notification.notification_title}</NotificationTitle>
            <NotificationTime>{new Date(notification.created_on).toLocaleString()}</NotificationTime>
          </CardHeader>
          <NotificationBody>
            {notification.notification_message}
          </NotificationBody>
          <NotificationFooter>
            {/* Displaying available info, adjusting based on actual data structure */}
            {notification.send_by ? `Sent by: ${notification.send_by.first_name} ${notification.send_by.last_name}` : ''}
          </NotificationFooter>
        </NotificationCard>
      ))}
      {notifications.length === 0 && !loading && (
        <div style={{ padding: '20px', textAlign: 'center', color: '#888', fontStyle: 'italic' }}>
          No notifications found.
        </div>
      )}
      <div ref={observerTarget} style={{ height: '20px', margin: '20px 0', textAlign: 'center' }}>
        {loading && 'Loading more notifications...'}
      </div>
    </PageContainer>
  );
};

export default FacilityNotification;
